import { Cell, CellValue } from '@/shared/ui/data/EditableGrid/types/cell.types';
import { Row } from '@/shared/ui/data/EditableGrid/types/row.types';
import { Column } from '@/shared/ui/data/EditableGrid/types/column.types';
import { useMemo } from 'react';
import { createPosition } from '@/shared/ui/data/EditableGrid/lib/position';
import { buildMetadata, createCell } from '@/shared/ui/data/EditableGrid/lib/cell';

interface IOptions<T = Record<string, CellValue>> {
  currentRows: ReadonlyArray<Row<T>>;
  prevRows?: ReadonlyArray<Row<T>>;
  columns: ReadonlyArray<Column>;
  enableDiff: boolean;
}

export function useCellDiff<T extends Record<string, CellValue>>(options: IOptions<T>): Cell[][] {
  const { currentRows, prevRows, columns, enableDiff } = options;

  return useMemo(() => {
    if (!enableDiff || !prevRows) {
      return currentRows.map((row, rowIndex) => {
        return columns.map((col, colIndex) => {
          const position = createPosition(rowIndex, colIndex);
          const value: CellValue = row.data[col.id];

          const editable = col.editable ?? true;
          const cell = createCell(position, value, undefined, editable);

          const metadata = buildMetadata(col);
          return { ...cell, metadata } as Cell;
        });
      });
    }

    const prevRowMap = new Map(prevRows?.map((r) => [r.id, r]));

    return currentRows.map((row, rowIndex) => {
      const prevRow = prevRowMap.get(row.id);

      return columns.map((col, colIndex) => {
        const position = createPosition(rowIndex, colIndex);
        const current: CellValue = row.data[col.id];
        const prev: CellValue | undefined = prevRow?.data[col.id];

        const editable = col.editable ?? true;
        const cell = createCell(position, current, prev, editable);

        const metadata = buildMetadata(col);
        return { ...cell, metadata } as Cell;
      });
    });
  }, [currentRows, prevRows, columns, enableDiff]);
}
